import { useEffect, useMemo, useState } from "react"
import { shallowEqual, useSelector } from "react-redux";
import SimpleTable from "my-simple-react-table";
import SVG from "react-inlinesvg";
import { Button } from "react-bootstrap";
import { toAbsoluteUrl } from "../../../../../../../../../../_metronic/_helpers";
import {
    formatAmount,
    formatDate,
    formatStatus,
    permissionAllow,
    permissionCssClass,
    transactionCat,
    transactionType,
    ucFirst,
    getTransactionBalanceAfter
} from "../../../../../../../../../util/Helpers"
import { Permissions, PermissionType } from "../../../../../../../../../util/Permissions";
import ViewTransactionModal from "./modal/ViewTransactionModal";
import AddTransactionModal from "./modal/AddTransactionModal";
import { useDownloadTransactionsReportMutation, useGetTransactionSummaryQuery } from "../../../../../../../../../../redux/apiSlice";
import cogoToast from "cogo-toast";
import { FilterGroup } from "../../../../../../../../Common/components/FilterGroup";

const Transactions = props => {
    /* Get Account Details from redux */
    const accountDetails = useSelector(({ selectedUser }) => {
        return selectedUser.data?.accounts.filter(acc => acc.accountId === props.match.params.accountId)[0]?.details;
    }, shallowEqual);

    const adminUser = useSelector(state => state.auth.user);
    const { accountId } = props.match.params;
    /* Transaction Modal State Management */
    const [transactionModal, setTransactionModal] = useState({ showViewTransactionModal: false, showAddTransactionModal: false, details: null });

    /* Modal show/hide Toggle */
    const toggleModal = modal => _ => {
        setTransactionModal({ ...transactionModal, [modal]: !transactionModal[modal] })
    }

    const formatActivity = row => {
        return <div className="d-flex align-items-center">
            <div className="symbol symbol-50 flex-shrink-0 mr-4">
                <span className="text-dark-75 font-weight-bolder text-hover-primary mb-1 font-size-lg">
                    {transactionType(row)}
                    <span className="text-muted pl-2">
                        {transactionCat(row)}
                    </span>
                </span>
            </div>
        </div>
    }


    /* Action Buttons */
    const transactionAction = row => {
        return <span style={{ whiteSpace: "nowrap" }}>
            <Button
                title="View Payment"
                className="btn btn-icon btn-light btn-hover-primary btn-sm bg-white"
                onClick={_ => setTransactionModal({ ...transactionModal, showViewTransactionModal: true, details: row })}
            >
                <span className="svg-icon svg-icon-md svg-icon-primary">
                    <SVG
                        title="View Transaction"
                        src={toAbsoluteUrl(
                            `/media/svg/icons/${(permissionAllow(adminUser, Permissions.TRANSACTIONS, PermissionType.UPDATE) && row.tranStatus === "pending") ? "Media/Play" : "General/Eye-2"}.svg`
                        )}
                    />
                </span>
            </Button>
        </span>
    };

    const [
        downloadTransactionsReport,
        {
            isLoading: isLoadingReport,
            isSuccess: isReportGenerating,
            error: errorReport,
        }
    ] = useDownloadTransactionsReportMutation()

    useEffect(() => {
        if (errorReport) {
            cogoToast.error(errorReport.data);
        }
    }, [errorReport])

    useEffect(() => {
        if (isReportGenerating) {
            cogoToast.success(`Transactions export in progress. The resulting file will be sent to your mail.`);
        }
    }, [isReportGenerating])

    const { data: transactionSummaryResponse, isFetching: isFetchingTransactionSummary } = useGetTransactionSummaryQuery(
        { accountId },
        { skip: !accountId }
    );

    const summaryData = useMemo(() => {
        const payload = transactionSummaryResponse?.data ?? transactionSummaryResponse ?? null;
        if (!payload || typeof payload !== "object") return null;
        return payload;
    }, [transactionSummaryResponse]);

    const summaryKeys = useMemo(() => ([
        { key: "deposit", label: "Deposit", bg: "success" },
        { key: "withdraw", label: "Withdrawal", bg: "danger" },
        { key: "intra_transfer_in", label: "Intra Transfer In", bg: "primary" },
        { key: "intra_transfer_out", label: "Intra Transfer Out", bg: "warning" },
        { key: "buy", label: "Buy", bg: "primary" },
        { key: "sell", label: "Sell", bg: "danger" },
        { key: "div", label: "Dividend", bg: "info" },
    ]), []);

    const renderTransactionSummaryRows = (bucket) => {
        if (isFetchingTransactionSummary) {
            return <span className="text-muted font-weight-bold font-size-sm">Loading...</span>;
        }
        if (!bucket || typeof bucket !== "object") {
            return <span className="text-muted font-weight-bold font-size-sm">No summary available</span>;
        }
        const currencies = Object.keys(bucket);
        if (currencies.length === 0) {
            return <span className="text-muted font-weight-bold font-size-sm">No summary available</span>;
        }
        return currencies.map((currency) => (
            <div key={currency} className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-dark-75 font-weight-bolder">{currency || "-"}</span>
                <span className="text-dark font-weight-bolder">{formatAmount(currency, bucket[currency])}</span>
            </div>
        ));
    };

    return (
        <FilterGroup defaultFilters={{ endDate: new Date().toISOString().split('T')[0], sortDirection: "desc", limit: "10" }}>
            <div className="row">
                <div className="col-12">
                    {/* <!--begin::List Widget 14--> */}
                    <div className="card card-custom gutter-b">
                        {/* <!--begin::Header--> */}
                        <div className="card-header border-0">
                            {/* <!--begin::Title--> */}
                            <div className="card-title pt-5 w-100">
                                <h3 className="card-label">
                                    <span className="d-block text-dark h3 font-weight-bolder">Transactions</span>
                                    <span className="d-block text-muted mt-2 font-size-sm">
                                        Cash inflow and outflow to the account
                                    </span>
                                </h3>
                                <div
                                    className={`card-toolbar ml-auto ${permissionCssClass(adminUser, Permissions.TRANSACTIONS, PermissionType.CREATE)}`}
                                >
                                    <button
                                        className="btn btn-primary font-weight-bolder font-size-sm"
                                        onClick={toggleModal("showAddTransactionModal")}
                                    >
                                        <span className="svg-icon svg-icon-md svg-icon-white">
                                            <SVG
                                                title=" "
                                                src={toAbsoluteUrl("/media/svg/icons/General/Expand-arrows.svg")}
                                            />
                                        </span>
                                        Add Transaction
                                    </button>
                                    <FilterGroup.Content>
                                        {({ filters }) => (
                                            <button
                                                type="button"
                                                className="btn btn-primary ml-4"
                                                disabled={isLoadingReport}
                                                onClick={() =>
                                                    downloadTransactionsReport({
                                                        userId: accountDetails.userId,
                                                        partner: accountDetails.partner,
                                                        liveData: true,
                                                        startDate: filters?.startDate || "",
                                                        endDate: filters?.endDate || "",
                                                        status: filters?.tranStatus || "",
                                                        type: filters?.tranType || "",
                                                        format: "csv",
                                                    })
                                                }
                                            >
                                                Export CSV
                                            </button>
                                        )}
                                    </FilterGroup.Content>

                                </div>
                            </div>
                        </div>
                        {/* <!--end::Header--> */}

                        <div className="px-6 pb-4">
                            <div className="row mb-2">
                                {summaryKeys.map(({ key, label, bg }) => (
                                    <div key={key} className="col-md-6 col-lg-4 mb-4">
                                        <div className={`card card-custom gutter-b bg-light-${bg} border-0 shadow-none h-100 mb-0`}>
                                            <div className="card-body py-5">
                                                <div className="font-size-sm text-muted font-weight-bold text-uppercase mb-2">Transaction Summary</div>
                                                <div className="font-size-h5 font-weight-bolder text-dark mb-4">{label}</div>
                                                {renderTransactionSummaryRows(summaryData?.[key])}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="col-12 mb-4">
                                <FilterGroup.DateRange />
                            </div>
                            <div className="col-12 mb-4 d-flex align-items-center">
                                <div className="mr-4">
                                    <FilterGroup.Filter name={"tranType"} label={"Activity"}>
                                        <FilterGroup.Option value={""}>All</FilterGroup.Option>
                                        <FilterGroup.Option value={"DEPOSIT"}>Deposits</FilterGroup.Option>
                                        <FilterGroup.Option value={"WITHDRAW"}>Withdrawals</FilterGroup.Option>
                                        <FilterGroup.Option value={"PORTFOLIO_CREATION"}>Portfolio Creations</FilterGroup.Option>
                                        <FilterGroup.Option value={"CARD_CREATION"}>Virtual Card Creations</FilterGroup.Option>
                                        <FilterGroup.Option value={"CARD_DEPOSIT"}>Virtual Card Deposits</FilterGroup.Option>
                                    </FilterGroup.Filter>
                                </div>
                                <div className="mr-4">
                                    <FilterGroup.Filter name={"tranStatus"} label={"Status"}>
                                        <FilterGroup.Option value={""}>All</FilterGroup.Option>
                                        <FilterGroup.Option value={"pending"}>Pending</FilterGroup.Option>
                                        <FilterGroup.Option value={"processing"}>Processing</FilterGroup.Option>
                                        <FilterGroup.Option value={"executed"}>Executed</FilterGroup.Option>
                                        <FilterGroup.Option value={"failed"}>Failed</FilterGroup.Option>
                                    </FilterGroup.Filter>
                                </div>
                                <div className="mr-4">
                                    <FilterGroup.Filter name={"sortDirection"} label={"Sort"}>
                                        <FilterGroup.Option value={"desc"}>Descending</FilterGroup.Option>
                                        <FilterGroup.Option value={"asc"}>Ascending</FilterGroup.Option>
                                    </FilterGroup.Filter>
                                </div>
                                <div>
                                    <FilterGroup.Filter name={"limit"} label={"Rows"}>
                                        <FilterGroup.Option value={"10"}>10</FilterGroup.Option>
                                        <FilterGroup.Option value={"50"}>50</FilterGroup.Option>
                                        <FilterGroup.Option value={"100"}>100</FilterGroup.Option>
                                    </FilterGroup.Filter>
                                </div>
                            </div>
                        </div>

                        {/* <!--begin::Body--> */}
                        <div className="card-body pt-0">
                            <style>
                                {`
                                    .transactions-scroll-table .table-responsive {
                                        max-height: 52rem;
                                        overflow-y: auto;
                                    }
                                `}
                            </style>
                            <div className="transactions-scroll-table">
                                <FilterGroup.Content>
                                    {({ filters }) => {
                                        const filteredTransactions = (Array.isArray(accountDetails?.transactions) ? accountDetails.transactions : [])
                                            .filter((transaction) => {
                                                const transactionDate = new Date(transaction.created);
                                                const startDate = filters?.startDate ? new Date(filters.startDate) : null;
                                                const endDate = filters?.endDate ? new Date(filters.endDate).setHours(23, 59, 59, 999) : null;
                                                const tranType = filters?.tranType;
                                                const tranStatus = `${filters?.tranStatus || ""}`.trim().toLowerCase();
                                                const transactionStatus = `${transaction?.tranStatus || ""}`.trim().toLowerCase();

                                                return (
                                                    (!startDate || transactionDate >= startDate) &&
                                                    (!endDate || transactionDate <= endDate) &&
                                                    (!tranType || transaction.tranType === tranType) &&
                                                    (!tranStatus || transactionStatus === tranStatus)
                                                );
                                            })
                                            .sort((first, second) => {
                                                const firstDate = new Date(first?.created).getTime() || 0;
                                                const secondDate = new Date(second?.created).getTime() || 0;
                                                return filters?.sortDirection === "asc"
                                                    ? firstDate - secondDate
                                                    : secondDate - firstDate;
                                            });

                                        return (
                                            <SimpleTable
                                                data={filteredTransactions}
                                                rowPerPage={Number(filters?.limit || 10)}
                                                columns={[
                                                    { title: "ACTIVITY", format: formatActivity },
                                                    { title: "ID", key: "autoId" },
                                                    { title: "REFERENCE", key: "refId" },
                                                    { title: "DESCRIPTION", key: "comment" },
                                                    { title: "AMOUNT", format: row => formatAmount(accountDetails.currency, row.tranAmount) },
                                                    { title: "STATUS", format: row => formatStatus(ucFirst(row.tranStatus)) },
                                                    { title: "BALANCE BEFORE", format: row => formatAmount(row.currency, row.accountBal) },
                                                    { title: "BALANCE AFTER", format: row => formatAmount(row.currency, getTransactionBalanceAfter(row)) },
                                                    { title: "DATE", format: row => formatDate(row.created) },
                                                    { title: "", format: transactionAction }
                                                ]}
                                            />
                                        );
                                    }}
                                </FilterGroup.Content>
                            </div>
                        </div>

                    </div>
                    {/* <!--end::List Widget 14--> */}
                </div>


                {transactionModal.showAddTransactionModal && <AddTransactionModal {...transactionModal} {...props} currency={accountDetails?.currency} partner={accountDetails?.partner} onHide={toggleModal("showAddTransactionModal")} />}
                {transactionModal.showViewTransactionModal && <ViewTransactionModal {...transactionModal} onHide={toggleModal("showViewTransactionModal")} />}
            </div>
        </FilterGroup>
    );

}

export default Transactions;
